/**
 * The frozen mixer: stages 3 and 4, in both of the places they have to run.
 *
 * `propmap prod` baked stages 1 and 2 -- measure and normalise -- into one byte
 * per cell, so what is left for the browser is the part that actually reads the
 * sliders:
 *
 *     3. activate   g = each layer's percentile through its own curve, at a
 *                   gain of k * u, where u is that layer's slider on 0..1
 *     4. blend      the weighted power mean of those, painted while it clears
 *                   the cutoff
 *
 * Both halves live here: the GLSL the map is drawn with, and the TypeScript the
 * area panel's score is computed with. They are deliberately the same
 * arithmetic written twice, because the moment they disagree the number in the
 * panel stops describing the colour under the reader's finger.
 *
 * `propmap/mixer.py` is the reference for every formula, and
 * `propmap/prod_selftest.py` holds this file to it through a fixture.
 *
 * **Reading a baked byte.** 0 means the layer has no answer there; 1..255 is a
 * percentile as `(byte - 1) / 254`. A grid is read between its cell centres
 * through a kernel that runs from nearest through bilinear to a cubic B-spline,
 * renormalised over the cells that have an answer, so no-data never drags a
 * neighbour down. How much of the kernel found an answer is the coverage, and
 * the two edge ramps below turn the hard in-or-out into a fade.
 */

import type { ActivationKind, BlendKind, BlendSpec, GridSpec } from "./manifest.ts";

/** `propmap.prod.LEVELS`: a percentile lands on 1..255, and 0 is no answer. */
export const LEVELS = 254;

export const ACTIVATIONS: readonly ActivationKind[] = ["power", "exponential", "logistic"];
export const BLENDS: readonly BlendKind[] = ["product", "geometric", "arithmetic", "power"];

/** What one byte of a baked raster says; NaN where there is no answer. */
export function score(byte: number): number {
  return byte > 0 ? (byte - 1) / LEVELS : NaN;
}

// ---------------------------------------------------------------------------
// reading a grid between its cells
// ---------------------------------------------------------------------------

export interface GridSample {
  /** the kernel-weighted mean over the cells with an answer; NaN if none */
  value: number;
  /** the share of the kernel that landed on cells with an answer */
  coverage: number;
}

/** Tap weights at offsets -1, 0, +1, +2 from the cell the point falls after. */
export function kernel(t: number, sampling: number, out: Float64Array): void {
  const s = Math.min(Math.max(sampling, 0), 2);
  const near1 = t < 0.5 ? 1 : 0;
  const near2 = t < 0.5 ? 0 : 1;
  const lin0 = 1 - t;
  const lin1 = t;
  if (s <= 1) {
    out[0] = 0;
    out[1] = near1 + s * (lin0 - near1);
    out[2] = near2 + s * (lin1 - near2);
    out[3] = 0;
    return;
  }
  const t2 = t * t;
  const t3 = t2 * t;
  const a = s - 1;
  const c0 = (1 - 3 * t + 3 * t2 - t3) / 6;
  const c1 = (4 - 6 * t2 + 3 * t3) / 6;
  const c2 = (1 + 3 * t + 3 * t2 - 3 * t3) / 6;
  const c3 = t3 / 6;
  out[0] = a * c0;
  out[1] = lin0 + a * (c1 - lin0);
  out[2] = lin1 + a * (c2 - lin1);
  out[3] = a * c3;
}

const kx = new Float64Array(4);
const ky = new Float64Array(4);

/**
 * A baked raster read at an OSGB point through the kernel.
 *
 * `bytes` is the layer as emitted, so a 0 is "no answer" and takes no part.
 */
export function sampleBaked(bytes: Uint8Array, grid: GridSpec, x: number, y: number,
                            sampling: number): GridSample {
  const u = (x - grid.x0) / grid.step;
  const v = (y - grid.y0) / grid.step;
  const bx = Math.floor(u);
  const by = Math.floor(v);
  kernel(u - bx, sampling, kx);
  kernel(v - by, sampling, ky);
  let value = 0;
  let coverage = 0;
  for (let j = 0; j < 4; j++) {
    const wy = ky[j]!;
    if (wy === 0) continue;
    const cy = by + j - 1;
    if (cy < 0 || cy >= grid.ny) continue;
    for (let i = 0; i < 4; i++) {
      const w = kx[i]! * wy;
      if (w === 0) continue;
      const cx = bx + i - 1;
      if (cx < 0 || cx >= grid.nx) continue;
      const byte = bytes[cy * grid.nx + cx]!;
      if (byte === 0) continue;
      coverage += w;
      value += w * ((byte - 1) / LEVELS);
    }
  }
  return { value: coverage > 0 ? value / coverage : NaN, coverage };
}

/** A presence raster, which holds everywhere on its grid, read the same way. */
export function samplePresence(bytes: Uint8Array, grid: GridSpec, x: number, y: number,
                               sampling: number): number {
  const u = (x - grid.x0) / grid.step;
  const v = (y - grid.y0) / grid.step;
  const bx = Math.floor(u);
  const by = Math.floor(v);
  kernel(u - bx, sampling, kx);
  kernel(v - by, sampling, ky);
  let value = 0;
  let weight = 0;
  for (let j = 0; j < 4; j++) {
    const wy = ky[j]!;
    if (wy === 0) continue;
    const cy = by + j - 1;
    if (cy < 0 || cy >= grid.ny) continue;
    for (let i = 0; i < 4; i++) {
      const w = kx[i]! * wy;
      if (w === 0) continue;
      const cx = bx + i - 1;
      if (cx < 0 || cx >= grid.nx) continue;
      weight += w;
      value += w * (bytes[cy * grid.nx + cx]! / 255);
    }
  }
  return weight > 0 ? value / weight : 1;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(Math.max((x - e0) / (e1 - e0), 0), 1);
  return t * t * (3 - 2 * t);
}

/** How much a layer has an answer here, from how much of the kernel found one. */
export function edgeStep(coverage: number, soft: number): number {
  if (soft <= 0) return coverage >= 0.5 ? 1 : 0;
  return smoothstep(0.5 - soft, 0.5 + soft, coverage);
}

/** How far past the cutoff a place is drawn at full strength; nothing below it. */
export function fadeStep(margin: number, fade: number): number {
  if (margin < 0) return 0;
  if (fade <= 0) return 1;
  return smoothstep(0, fade, margin);
}

// ---------------------------------------------------------------------------
// 3. activate
// ---------------------------------------------------------------------------

/** `k * u`, with `u` the slider on 0..1. */
export function gain(weight: number, k: number, weightMax: number): number {
  const u = Math.min(Math.max(weight / weightMax, 0), 1);
  return k * u;
}

/**
 * A percentile through one layer's curve.
 *
 * All three are on 0..1, never score a better percentile lower, and are flat at
 * a slider of zero -- so a layer nobody asked for says nothing about anywhere,
 * and a rising slider only ever makes it more selective.
 */
export function activate(s: number, kind: ActivationKind, g: number): number {
  const v = Math.min(Math.max(s, 0), 1);
  if (kind === "power") return Math.pow(v, g);
  if (kind === "exponential") return Math.exp(-g * (1 - v));
  return 1 / (1 + Math.exp(-g * (v - 0.5)));
}

// ---------------------------------------------------------------------------
// 4. blend
// ---------------------------------------------------------------------------

export function blendStart(): number {
  return 0;
}

export function blendAdd(acc: number, g: number, w: number, spec: BlendSpec): number {
  const kind = effectiveBlend(spec);
  const lo = Math.max(g, spec.blend_floor);
  if (kind === "product") return acc + Math.log(lo);
  if (kind === "geometric") return acc + w * Math.log(lo);
  if (kind === "arithmetic") return acc + w * g;
  return acc + w * Math.pow(lo, spec.power_p);
}

export function blendFinish(acc: number, total: number, spec: BlendSpec): number {
  const kind = effectiveBlend(spec);
  if (kind === "product") return Math.exp(acc);
  if (kind === "geometric") return Math.exp(acc / total);
  if (kind === "arithmetic") return acc / total;
  return Math.pow(acc / total, 1 / spec.power_p);
}

/** The power mean at `p = 0` is the geometric mean, and would divide by zero. */
function effectiveBlend(spec: BlendSpec): BlendKind {
  return spec.blend === "power" && Math.abs(spec.power_p) < 1e-6 ? "geometric" : spec.blend;
}

/** Where a blend value sits on the ramp, for colour only. */
export function rampPosition(value: number, spec: BlendSpec): number {
  const v = Math.min(Math.max(value, 0), 1);
  return spec.ramp_floor + (1 - spec.ramp_floor) * Math.pow(v, spec.display_gamma);
}

/** One weighted layer as read at a point, before the blend. */
export interface LayerRead {
  name: string;
  weight: number;
  activation: ActivationKind;
  k: number;
  value: number;
  coverage: number;
  /** how far the point is inside this layer's footprint; 1 without one */
  presence: number;
  /** what this layer's footprint says outside it; empty without one */
  outsideLabel: string;
}

export interface Mixed {
  /** the blend, or NaN where nothing is painted */
  value: number;
  /** every weighted layer has an answer here */
  known: boolean;
  /** the blend cleared the cutoff, and the point is inside every footprint */
  painted: boolean;
  /**
   * The weighted layers that took this place off the map, worst first, and
   * empty where the cutoff is not what kept it off.
   */
  ruledOutBy: string[];
  /** what the footprint the point is furthest outside says, when it is outside */
  outside: string | null;
}

/**
 * The blend at one point.
 *
 * Kept parallel to the shader below. A place is painted only where every
 * weighted layer has an answer, the blend clears the cutoff, and the point is
 * inside the footprint of every weighted layer -- the three reasons a cell goes
 * unpainted, and the difference between them is the point of the map.
 */
export function mix(reads: readonly LayerRead[], spec: BlendSpec): Mixed {
  let acc = blendStart();
  let total = 0;
  let known = true;
  let presence = 1;
  let lowest = { name: "", g: Infinity };
  let furthest = { label: "", presence: 1 };
  const activated: { name: string; g: number }[] = [];

  for (const read of reads) {
    if (read.weight <= 0) continue;
    presence *= read.presence;
    if (read.outsideLabel && read.presence < furthest.presence) {
      furthest = { label: read.outsideLabel, presence: read.presence };
    }
    if (edgeStep(read.coverage, spec.edge_soft) < 0.5) known = false;
    if (read.coverage <= 0) continue;
    const g = activate(read.value, read.activation, gain(read.weight, read.k, spec.weight_max));
    if (g < lowest.g) lowest = { name: read.name, g };
    activated.push({ name: read.name, g });
    const w = read.weight / spec.weight_max;
    acc = blendAdd(acc, g, w, spec);
    total += w;
  }

  if (total === 0) {
    return { value: NaN, known: false, painted: false, ruledOutBy: [], outside: null };
  }
  const value = blendFinish(acc, total, spec);
  const outside = presence < 0.5 ? furthest.label || null : null;
  const painted = known && value >= spec.cutoff && presence >= 0.5;
  const ruledOutBy = known && value < spec.cutoff && lowest.name
    ? blame(activated, spec.cutoff, lowest.name) : [];
  return { value: painted ? value : NaN, known, painted, ruledOutBy, outside };
}

/**
 * Which layers to name when the cutoff took a place off the map.
 *
 * A place fails because the blend is under the cutoff, and the blend is over
 * every weighted layer, so strictly they all had a hand in it. What a reader
 * wants is the ones that were actually bad here: the layers whose own activated
 * score is itself under the cutoff, worst first. A blend can fall short with
 * nothing individually under it -- several middling layers, none of them the
 * culprit -- and then the lowest is named alone rather than the whole list,
 * which would read as an accusation against every category at once.
 */
function blame(activated: readonly { name: string; g: number }[], cutoff: number,
                lowest: string): string[] {
  const under = activated.filter((a) => a.g < cutoff);
  if (!under.length) return [lowest];
  return [...under].sort((a, b) => a.g - b.g).map((a) => a.name);
}

// ---------------------------------------------------------------------------
// the same arithmetic, for the fragment shader
// ---------------------------------------------------------------------------

/** The kernel and the two edge ramps. */
export const SAMPLING_GLSL = `
uniform float u_sampling;
uniform float u_edgeSoft;
uniform float u_edgeFade;

vec4 kernel(float t) {
  vec4 nearest = t < 0.5 ? vec4(0.0, 1.0, 0.0, 0.0) : vec4(0.0, 0.0, 1.0, 0.0);
  vec4 lin = vec4(0.0, 1.0 - t, t, 0.0);
  float s = clamp(u_sampling, 0.0, 2.0);
  if (s <= 1.0) return mix(nearest, lin, s);
  float t2 = t * t, t3 = t2 * t;
  vec4 cubic = vec4(1.0 - 3.0 * t + 3.0 * t2 - t3, 4.0 - 6.0 * t2 + 3.0 * t3,
                    1.0 + 3.0 * t + 3.0 * t2 - 3.0 * t3, t3) / 6.0;
  return mix(lin, cubic, s - 1.0);
}

float edgeStep(float coverage) {
  if (u_edgeSoft <= 0.0) return coverage >= 0.5 ? 1.0 : 0.0;
  return smoothstep(0.5 - u_edgeSoft, 0.5 + u_edgeSoft, coverage);
}

float fadeStep(float margin) {
  if (margin < 0.0) return 0.0;
  if (u_edgeFade <= 0.0) return 1.0;
  return smoothstep(0.0, u_edgeFade, margin);
}
`;

/**
 * The activation and the blend, as uniforms rather than branches on names.
 *
 * `u_blend` and each layer's `u_activation` are indices into `BLENDS` and
 * `ACTIVATIONS`, so the shader is compiled once and never regenerated when a
 * reader moves a slider -- there is nothing to regenerate, because a slider is
 * a float.
 */
export const MIXER_GLSL = `
uniform int   u_blend;
uniform float u_p;
uniform float u_blendFloor;

float activate(float s, int kind, float g) {
  float v = clamp(s, 0.0, 1.0);
  if (kind == 0) return pow(v, g);
  if (kind == 1) return exp(-g * (1.0 - v));
  return 1.0 / (1.0 + exp(-g * (v - 0.5)));
}

float blendAdd(float acc, float g, float w) {
  float lo = max(g, u_blendFloor);
  if (u_blend == 0) return acc + log(lo);
  if (u_blend == 1) return acc + w * log(lo);
  if (u_blend == 2) return acc + w * g;
  return acc + w * pow(lo, u_p);
}

float blendFinish(float acc, float total) {
  if (u_blend == 0) return exp(acc);
  if (u_blend == 1) return exp(acc / total);
  if (u_blend == 2) return acc / total;
  return pow(acc / total, 1.0 / u_p);
}
`;
